import React, { useState, useEffect } from 'react';
import { Modal, List, Spin, Alert } from 'antd';
import { useTranslation } from 'react-i18next';
import { getRecruiterTargetHistory, RecruiterTargetHistoryEntry } from '../../api/jobs'; // Adjust path
import Button from '../../UI/buttons/Button';
import moment from 'moment'; // For date formatting

interface TargetHistoryModalProps {
    isVisible: boolean;
    jobId: string | null;
    assignmentId: string | null;
    onClose: () => void;
}

const TargetHistoryModal: React.FC<TargetHistoryModalProps> = ({
    isVisible,
    jobId,
    assignmentId,
    onClose,
}) => {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [history, setHistory] = useState<RecruiterTargetHistoryEntry[]>([]);

    useEffect(() => {
        if (isVisible && jobId && assignmentId) {
            setLoading(true);
            setError(null);
            setHistory([]); // Clear previous history
            getRecruiterTargetHistory({ jobId, assignmentId })
                .then((response) => {
                    setHistory(response.data.results); // Assuming pagination
                })
                .catch((err) => {
                    console.error("Error fetching target history:", err);
                    setError(t('ERROR_FETCHING_HISTORY', 'Could not load target history.'));
                })
                .finally(() => {
                    setLoading(false);
                });
        }
    }, [isVisible, jobId, assignmentId, t]);

    const renderItem = (item: RecruiterTargetHistoryEntry) => {
        const targetTypeText = item.target_type
            ? item.target_type === 'hires'
                ? t('TARGET_TYPE_HIRES', 'Hires')
                : t('TARGET_TYPE_INTRODUCTIONS', 'Introductions')
            : t('TARGET_TYPE_NONE', 'None');
        const targetValueText = item.target_value !== null ? item.target_value : '-';
        const setByText = item.set_by?.name || t('USER_UNKNOWN', 'Unknown User');
        const dateText = moment(item.created_at).format('YYYY-MM-DD HH:mm');

        return (
            <List.Item>
                <List.Item.Meta
                    title={`${t('TARGET_HISTORY_SET_AT', 'Set at')}: ${dateText} ${t('TARGET_HISTORY_BY', 'by')} ${setByText}`}
                    description={`${t('TARGET_HISTORY_TYPE', 'Type')}: ${targetTypeText}, ${t('TARGET_HISTORY_VALUE', 'Value')}: ${targetValueText}`}
                />
            </List.Item>
        );
    };

    return (
        <Modal
            title={t('TARGET_HISTORY_TITLE', 'Target History')}
            visible={isVisible}
            onCancel={onClose}
            footer={[
                <Button key="close" onClick={onClose}>
                    {t('CLOSE', 'Close')}
                </Button>,
            ]}
            width={600}
        >
            {loading && <Spin size="large" style={{ display: 'block', margin: '20px auto' }} />}
            {error && <Alert message={error} type="error" showIcon style={{ marginBottom: '20px' }} />}
            {!loading && !error && (
                <List
                    itemLayout="horizontal"
                    dataSource={history}
                    renderItem={renderItem}
                    locale={{ emptyText: t('TARGET_HISTORY_EMPTY', 'No target history found.') }}
                />
            )}
        </Modal>
    );
};

export default TargetHistoryModal; 